import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import "../../assets/css/Integrations/Invar.css";
import { useApi } from "../../Providers/ApiProvider";
import { MonitoredWorkOrderResponse } from "../../data/InvarDataModels/MonitoredWorkOrderResponse";
import { ConveyanceResponse } from "../../data/InvarDataModels/ConveyanceResponse";
import { InvarMonitorCard } from "./InvarMonitorCard";
import {
  Mondrian,
  MondrianColumn,
  MondrianInputObject,
} from "@spt/mondrian/exports";

export const InvarMonitorAll = () => {
  const [MonitorConveyanceIDs, setMonitorConveyanceIDs] = useState(
    Array<number>,
  );
  const [MonitorOrderBySlowest, setMonitorOrderBySlowest] = useState(false);
  const [MonitorOrderByRecent, setMonitorOrderByRecent] = useState(true);
  const [MonitorLimit, setMonitorLimit] = useState(100);
  const [MonitorFrom, setMonitorFrom] = useState(undefined);
  const [MonitorUntil, setMonitorUntil] = useState(undefined);
  const [MonitorSearchTerm, setMonitorSearchTerm] = useState("");

  const [SearchStale, setSearchStale] = useState(false);

  const refTimeoutProcessing = useRef(null);
  const refTimeoutStale = useRef(null);
  const refTimeoutAll = useRef(null);

  const [theseWorkOrders, setTheseWorkOrders] = useState(
    Array<MonitoredWorkOrderResponse>,
  );
  const [theseConveyances, setTheseConveyances] = useState(
    Array<ConveyanceResponse>,
  );

  const [mutatedWorkOrders, setMutatedWorkOrders] = useState(
    Array<MondrianInputObject>,
  );

  const [Loading, setLoading] = useState(true);

  const columns: MondrianColumn[] = [
    {
      HumanName: "Work Order",
      MachineName: "WorkOrder",
      Filterable: false,
      Sortable: false,
      Groupable: false,
      Hideable: false,
    },
    {
      HumanName: "_ROW_PROCESSING",
      MachineName: "_ROW_PROCESSING",
      Filterable: false,
      Sortable: false,
      Groupable: false,
      Hideable: false,
    },
  ];

  const DA = useApi();
  const IDA = DA.Invar;

  useEffect(() => {
    if (IDA !== undefined) {
      if (theseConveyances === undefined || theseConveyances.length === 0) {
        LoadConveyances();
      }
      Load();
    }
  }, [IDA]);

  const StatefullyMergeWorkOrderResponse = (
    MWOR: MonitoredWorkOrderResponse,
  ) => {
    setTheseWorkOrders(
      [...theseWorkOrders]
        .filter((th) => th.WorkOrderID !== MWOR.WorkOrderID)
        .concat(MWOR),
    );
  };

  const StatefullyMergeWorkOrderResponses = (
    MWORs: MonitoredWorkOrderResponse[],
  ) => {
    setTheseWorkOrders(
      [...theseWorkOrders]
        .filter(
          (th) => !MWORs.some((ith) => ith.WorkOrderID === th.WorkOrderID),
        )
        .concat(MWORs),
    );
  };

  const Retry = async (WOID: number) => {
    StatefullyMergeWorkOrderResponse(await IDA.RefireMonitoredWorkOrder(WOID));
  };

  const Cancel = async (WOID: number) => {
    StatefullyMergeWorkOrderResponse(await IDA.CancelMonitoredWorkOrder(WOID));
  };

  const Archive = async (WOID: number) => {
    await IDA.ArchiveMonitoredWorkOrder(WOID);

    setTheseWorkOrders(
      [...theseWorkOrders].filter((th) => th.WorkOrderID !== WOID),
    );
  };

  const Clone = async (WOID: number) => {
    IDA.CloneMonitoredWorkOrder(WOID).then((data) => {
      setTheseWorkOrders([...theseWorkOrders].concat(data));
    });
  };

  const Activate = async (WOID: number) => {
    StatefullyMergeWorkOrderResponse(
      await IDA.ActivateMonitoredWorkOrder(WOID),
    );
  };

  const PollWorkOrders = async (
    woids: MonitoredWorkOrderResponse[],
    relay: boolean,
  ) => {
    const _wors: MonitoredWorkOrderResponse[] = [];
    var tasks = [];
    woids.forEach((th) => {
      tasks.push(
        IDA.MonitorThisWorkOrder(th.WorkOrderID).then((data) => {
          _wors.push(data);
        }),
      );
    });
    await Promise.all(tasks);
    StatefullyMergeWorkOrderResponses(_wors);
    if (relay) {
      RegenerateTimers();
    }
  };

  const RegenerateTimers = () => {
    // console.log("RegenerateTimers");
    // console.log(refTimeoutProcessing);
    // console.log(refTimeoutStale);
    // console.log(refTimeoutAll);

    if (refTimeoutProcessing !== undefined) {
      if (refTimeoutProcessing.current !== undefined) {
        clearTimeout(refTimeoutProcessing.current);
        refTimeoutProcessing.current = undefined;
      }
    }

    if (refTimeoutStale !== undefined) {
      if (refTimeoutStale.current !== undefined) {
        clearTimeout(refTimeoutStale.current);
        refTimeoutStale.current = undefined;
      }
    }

    if (refTimeoutAll !== undefined) {
      if (refTimeoutAll.current !== undefined) {
        clearTimeout(refTimeoutAll.current);
        refTimeoutAll.current = undefined;
      }
    }

    var Processings: MonitoredWorkOrderResponse[] = [];

    // for each work order marked as Processing, we register a new Interval with JS to refresh that particular Work Order after 15s.
    // console.log(audits);

    theseWorkOrders.forEach((th) => {
      if (th.Active) {
        // console.log("RegenerateTimers - proc +1");
        Processings.push(th);
      }
    });

    // console.log(Processings);
    if (Processings.length > 0) {
      refTimeoutProcessing.current = setTimeout(async () => {
        PollWorkOrders(Processings, true);
      }, 5000);
    }

    if (Processings.length === 0) {
      refTimeoutAll.current = setTimeout(async () => {
        Load();
        RegenerateTimers();
      }, 15000);
    }

    // console.log(refTimeoutProcessing);
    // console.log(refTimeoutStale);
    // console.log(refTimeoutAll);
  };

  useEffect(() => {
    setMutatedWorkOrders(MutateWorkOrders(theseWorkOrders));
    setSearchStale(false);
  }, [theseWorkOrders]);

  const MutateWorkOrders = (
    workOrders: MonitoredWorkOrderResponse[],
  ): MondrianInputObject[] => {
    let returner: MondrianInputObject[] = [];

    let _swo = [...workOrders];

    if (MonitorOrderByRecent === true) {
      _swo = _swo.sort((_left, _right) => {
        return _left.Created < _right.Created
          ? 1
          : _left.Created > _right.Created
            ? -1
            : 0;
      });
    } else if (MonitorOrderBySlowest === true) {
      _swo = _swo.sort((_left, _right) => {
        let _left_comp = _left.Completed ?? new Date(Date.now());
        let _right_comp = _right.Completed ?? new Date(Date.now());
        let _left_dur = _left_comp.getTime() - _left.Created.getTime();
        let _right_dur = _right_comp.getTime() - _right.Created.getTime();
        return _left_dur < _right_dur ? 1 : _left_dur > _right_dur ? -1 : 0;
      });
    }

    _swo = _swo.sort((_left, _right) => {
      // console.log('sort molecule')
      // console.log("_left")
      // console.log(_left)
      // console.log(_left.Poison)
      // console.log("_right")
      // console.log(_right)
      // console.log(_right.Poison)
      let ret =
        _left.Poison === true && _right.Poison === false
          ? 1
          : _right.Poison === true && _left.Poison === false
            ? -1
            : 0;
      // console.log("result");
      // console.log(ret);
      return ret;
    });

    _swo.forEach((th) => {
      let SV: number | dayjs.Dayjs = 0;

      if (MonitorOrderByRecent) {
        let cre = dayjs(th.Created);
        let cmp = dayjs(new Date(Date.now()));
        if (th.Poison) {
          cre = cre.add(1, "month");
        }
        SV = cmp.diff(cre);
      } else if (MonitorOrderBySlowest) {
        let cre = dayjs(th.Created);
        let cmp = dayjs(th.Completed ?? new Date(Date.now()));
        if (th.Poison) {
          cmp = cmp.add(1, "month");
        }
        SV = cmp.diff(cre);
      } else {
        SV = th.WorkOrderID;
      }

      // if (th.Poison) {
      // console.log("calc'd SV for POISON element:");
      // } else {
      // console.log("calc'd SV for element:");
      // }
      // console.log(SV);

      returner.push({
        WorkOrder: {
          _TYPE: "Element",
          ElementValue: (
            <InvarMonitorCard
              WorkOrder={th}
              Conveyance={theseConveyances.find(
                (ith) => ith.ConveyanceID === th.ConveyanceID,
              )}
              ShowView={true}
              Archive={Archive}
              Clone={Clone}
              Activate={Activate}
            />
          ),
          SortingValue: SV,
          GroupingValue: th.WorkOrderID,
        },
      });
    });

    return returner;
  };

  async function LoadConveyances() {
    setTheseConveyances(await IDA.GetConveyances());
  }

  async function Load() {
    setTheseWorkOrders(
      await IDA.MonitorWorkOrders(
        MonitorOrderBySlowest,
        MonitorOrderByRecent,
        MonitorLimit,
        MonitorConveyanceIDs,
        MonitorFrom?.toDate(),
        MonitorUntil?.toDate(),
        MonitorSearchTerm,
      ),
    );
    setLoading(false);
  }

  return (
    <div className="InvarMonitorAll">
      <div className="ParamBar">
        <div className="ParamFlex">
          <h2>Monitor All</h2>
          <hr />
          <h4>Order By</h4>
          <div className="MultiToggle">
            <div
              className={"Toggle" + (MonitorOrderByRecent ? " Active" : "")}
              onClick={() => {
                var val = MonitorOrderByRecent;
                setSearchStale(true);
                setMonitorOrderByRecent(!val);
                setMonitorOrderBySlowest(val);
              }}
            >
              Recent
            </div>
            <div
              className={"Toggle" + (MonitorOrderBySlowest ? " Active" : "")}
              onClick={() => {
                var val = MonitorOrderBySlowest;
                setSearchStale(true);
                setMonitorOrderByRecent(val);
                setMonitorOrderBySlowest(!val);
              }}
            >
              Slowest
            </div>
          </div>
          <hr />
          <h4>Conveyances</h4>
          <select
            className="ConveyancesSelector"
            multiple={true}
            onChange={(e) => {
              const newCIDS: number[] = [];
              // console.log(e.target.selectedOptions);
              for (var v = 0; v < e.target.selectedOptions.length; v++) {
                // console.log(e.target.selectedOptions[v]);
                // console.log(e.target.selectedOptions[v].value);
                newCIDS.push(
                  Number.parseInt(e.target.selectedOptions[v].value),
                );
              }
              setMonitorConveyanceIDs(newCIDS);
              setSearchStale(true);
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                Load();
              }
            }}
          >
            {theseConveyances.map((th, i) => {
              return (
                <option key={"conv_" + i.toString()} value={th.ConveyanceID}>
                  {th.Name}
                </option>
              );
            })}
          </select>
          <hr />
          <div className="Group">
            <label htmlFor="searchinput" className="Plain">
              Search Term:
            </label>
            <input
              type="text"
              id="searchinput"
              className="SearchText"
              placeholder="Search Term"
              value={MonitorSearchTerm}
              onChange={(e) => {
                setSearchStale(true);
                setMonitorSearchTerm(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  Load();
                }
              }}
            ></input>
          </div>
          <div className="Group">
            <label htmlFor="limitinput" className="Plain">
              Limit:
            </label>
            <input
              id="limitinput"
              type="numeric"
              className="LimitText"
              value={MonitorLimit}
              onChange={(e) => {
                setSearchStale(true);
                var tex = e.target.value;
                var num = 0;
                if (tex === "") {
                  num = 100;
                } else {
                  num = Number.parseInt(tex);
                }
                setMonitorLimit(num);
              }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  Load();
                }
              }}
            ></input>
          </div>
          <hr />
          <h4>Time Window</h4>
          <div className="MultiToggle">
            <div className="Plain">Start</div>
            <div
              className={
                "Toggle" + (MonitorFrom === undefined ? " Active" : "")
              }
              onClick={() => {
                if (MonitorFrom !== undefined) {
                  setSearchStale(true);
                  setMonitorFrom(undefined);
                }
              }}
            >
              Null
            </div>
            <div
              className="Action"
              onClick={() => {
                setSearchStale(true);
                setMonitorFrom(dayjs().subtract(1, "hour"));
              }}
            >
              Now - 1h
            </div>
          </div>
          <input
            className="dateinput"
            id="VisibleFrom"
            type="datetime-local"
            value={MonitorFrom?.local().format("YYYY-MM-DDTHH:mm") ?? ""}
            onChange={(e) => {
              setSearchStale(true);
              setMonitorFrom(dayjs(e.target.value));
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                Load();
              }
            }}
          />
          <div className="MultiToggle">
            <div className="Plain">Finish</div>
            <div
              className={
                "Toggle" + (MonitorUntil === undefined ? " Active" : "")
              }
              onClick={() => {
                if (MonitorUntil !== undefined) {
                  setSearchStale(true);
                  setMonitorUntil(undefined);
                }
              }}
            >
              Null
            </div>
            <div
              className="Action"
              onClick={() => {
                setSearchStale(true);
                setMonitorUntil(dayjs());
              }}
            >
              Now
            </div>
          </div>
          <input
            className="dateinput"
            id="VisibleFrom"
            type="datetime-local"
            value={MonitorUntil?.local().format("YYYY-MM-DDTHH:mm") ?? ""}
            onChange={(e) => {
              setSearchStale(true);
              setMonitorUntil(dayjs(e.target.value));
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                Load();
              }
            }}
          />
          <hr />
          <button
            className={SearchStale && !Loading ? "Highlight" : ""}
            onClick={() => {
              setLoading(true);
              Load();
            }}
            disabled={!SearchStale || Loading}
          >
            Refresh
          </button>
        </div>
      </div>
      <div className="Results">
        {Loading ? (
          <div className="LoadingPanel Block"></div>
        ) : (
          <Mondrian
            Name="InvarMonitorAll"
            Caption={false}
            Debug={false}
            InputObjects={mutatedWorkOrders}
            Columns={columns}
            LoadingFlag={Loading}
            Settings={{
              DisplaySettings: { DisplayColumnHeaders: false },
              FilterSettings: {
                Defaults: [],
              },
              GroupAndSortSettings: {
                ShowSubcount: true,
                Defaults: [],
              },
            }}
          ></Mondrian>
        )}
      </div>
    </div>
  );
};
